import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {Truncate} from "components/src/common/Truncate";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {Base64} from "components/src/thirdparty/Base64";
import {Assert} from "components/src/utils/Assert";
import {DateUtils} from "components/src/utils/DateUtils";
import {Utils} from "components/src/utils/Utils";
import {
    CloudDownload as DownloadIcon,
    EditPencil as EditIcon,
    Eye as ViewIcon,
    WrapText as MultiLineIcon
} from "iconoir-react";
import {useRef, useState} from 'react'
import {Accordion, Popover} from "react-bootstrap";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import {useTranslation} from 'react-i18next';
import {AllowedRecipientsEditorPopup} from "./AllowedRecipientsEditorPopup";
import {MultilineStringEditorPopup} from "./MultilineStringEditorPopup";
import {PropertyValueWithUserPropertyDetails} from "./PropertyValueWithUserPropertyDetails";
import './UserProperty.scss'

export type OnPropertyChange = (propertyName : string, value : string | undefined) => void

export interface UserPropertyProps {
    propertyDetails : PropertyValueWithUserPropertyDetails,
    onPropertyChange : OnPropertyChange,
    errorMessage : string | undefined
}

export const UserProperty = (userPropertyProps : UserPropertyProps) =>
{
    const [value, setValue] = useState(userPropertyProps.propertyDetails.propertyValue?.value)
    const [isInherited, setIsInherited] = useState(userPropertyProps.propertyDetails.propertyValue.inherited)
    const propertyNodeRef = useRef<HTMLElement>(null)
    /**
     * If true, the multiline popup dialog will be shown
     */
    const [showMultilineEditor, setShowMultilineEditor] = useState<boolean>(false);
    /**
     * If true, the multiline editor will be read-only
     */
    const [multilineEditorReadOnly, setMultilineEditorReadOnly] = useState<boolean>(false);
    /**
     * If true, the allowed recipients popup dialog will be shown
     */
    const [showAllowedRecipientsEditor, setShowAllowedRecipientsEditor] = useState<boolean>(false);
    /**
     * If true, the allowed recipients editor will be read-only
     */
    const [allowedRecipientsEditorReadOnly, setAllowedRecipientsEditorReadOnly] = useState<boolean>(false);
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary property to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    const {t:tu} = useTranslation('userproperties');

    /*
     * Shows an alert if the error message is set, i.e., is defined
     */
    const RenderValidationError = (errorProps : {errorMessage: string | undefined}) =>
    {
        if (errorProps.errorMessage != undefined)
        {
            // scroll to field with the validation failure
            propertyNodeRef.current?.scrollIntoView();

            return (
                <Alert variant="danger" className="mt-3">
                    {tu(errorProps.errorMessage)}
                </Alert>
            );
        }
    };

    const onChangeValue = (value :string) =>
    {
        setValue(value);

        userPropertyProps.onPropertyChange(userPropertyProps.propertyDetails.propertyValue.name, value);
    }

    const onChangeInherit = (inherited : boolean) =>
    {
        setIsInherited(inherited)

        if (inherited)
        {
            setValue(userPropertyProps.propertyDetails.propertyValue.parentValue);

            // notify parent that the value should be inherited
            // if the user unchecks inherit and then uncheck inherit again, the parent will have undefined value
            // whereas nothing was really changed. Instead of keeping track if all state in this component,
            // it's easier to do this in the parent since the parent can check whether a property value was
            // modified or not and skip unmodified values
            userPropertyProps.onPropertyChange(userPropertyProps.propertyDetails.propertyValue.name, undefined);
        }
        else {
            setValue(userPropertyProps.propertyDetails.propertyValue?.value);

            // notify parent
            userPropertyProps.onPropertyChange(userPropertyProps.propertyDetails.propertyValue.name,
                userPropertyProps.propertyDetails.propertyValue?.value ?? undefined);
        }
    }

    const renderCheckbox = () => {
        return(
            <Form.Check
                className="user-property-checkbox"
                id={userPropertyProps.propertyDetails.propertyValue.name}
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                checked={value === "true"}
                type="checkbox"
                onChange={e => onChangeValue(e.target.checked ? "true" : "false")}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
                label={t("common.enabled")}
            />
        )
    }

    /**
     * Renders a popup for viewing and editing the allowed recipients
     */
    const renderMultilineStringEditor = () =>
    {
        const onClose = (closeCause : DialogCloseCause, value?: string) =>
        {
            if (closeCause === DialogCloseCause.Succes) {
                onChangeValue(value ?? "");
            }

            setShowMultilineEditor(false);
        }

        return(
            <>
                <div className="d-flex flex-row gap-2">
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {setMultilineEditorReadOnly(true); setShowMultilineEditor(true)}}
                    >
                        <ViewIcon /> {t('user-property.view')}
                    </Button>
                    <Button
                        size="sm"
                        variant="success"
                        onClick={() => {setMultilineEditorReadOnly(false); setShowMultilineEditor(true)}}
                        disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                    >
                        <EditIcon /> {t('user-property.edit')}
                    </Button>
                </div>

                {showMultilineEditor &&
                    <MultilineStringEditorPopup
                        defaultValue={value}
                        readOnly={multilineEditorReadOnly}
                        userPropertyProps={userPropertyProps}
                        onClose={onClose} />}
            </>
        )
    }

    /**
     * Renders a popup for viewing and editing the allowed recipients
     */
    const renderAllowedRecipientsEditor = () =>
    {
        const onClose = (closeCause : DialogCloseCause, value?: string) =>
        {
            if (closeCause === DialogCloseCause.Succes) {
                onChangeValue(value ?? "");
            }

            setShowAllowedRecipientsEditor(false);
        }

        return(
            <>
                <div className="d-flex flex-row gap-2">
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {setAllowedRecipientsEditorReadOnly(true); setShowAllowedRecipientsEditor(true)}}
                    >
                        <ViewIcon /> {t('user-property.view')}
                    </Button>
                    <Button
                        size="sm"
                        variant="success"
                        onClick={() => {setAllowedRecipientsEditorReadOnly(false); setShowAllowedRecipientsEditor(true)}}
                        disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                    >
                        <EditIcon /> {t('user-property.edit')}
                    </Button>
                </div>

                {showAllowedRecipientsEditor &&
                    <AllowedRecipientsEditorPopup
                        defaultValue={value}
                        readOnly={allowedRecipientsEditorReadOnly}
                        userPropertyProps={userPropertyProps}
                        onClose={onClose} />}
            </>
        )
    }

    const renderPasswordEditor = () => {
        return (
            <Form.Control
                id={userPropertyProps.propertyDetails.propertyValue.name}
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                value={value ?? ""}
                type="password"
                autoComplete="new-password"
                onChange={e => onChangeValue(e.target.value)}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
            />
        )
    }

    const renderStringEditor = () => {
        return (
            <Form.Control
                id={userPropertyProps.propertyDetails.propertyValue.name}
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                value={value ?? ""}
                type="text"
                onChange={e => onChangeValue(e.target.value)}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
            />
        )
    }

    const renderNumberEditor = () => {
        return(
            <Form.Control
                id={userPropertyProps.propertyDetails.propertyValue.name}
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                value={value ?? ""}
                type="number"
                onChange={e => onChangeValue(Utils.numberToIntString(e.target.value))}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
            />
        )
    }

    const renderDateEditor = () => {
        return(
            <Form.Control
                id={userPropertyProps.propertyDetails.propertyValue.name}
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                value={DateUtils.timestampToISODateString(Number(value))}
                type="datetime-local"
                onChange={e => onChangeValue(DateUtils.isoDateStringToTimestamp(e.target.value).toString())}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
            />
        )
    }

    const renderEnumEditor = () => {
        return(
            <Form.Select
                aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                value={value ?? ""}
                onChange={e => onChangeValue(e.target.value)}
                disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                isInvalid={userPropertyProps.errorMessage !== undefined}
                ref={propertyNodeRef as React.RefObject<HTMLSelectElement>}
            >
                {/*render the enum options*/}
                {(userPropertyProps.propertyDetails.userPropertyDetails?.type?.options["values"] as string[]).map(e =>
                    <option key={e}>{e}</option>)}
            </Form.Select>
        )
    }

    /**
     * Renders an upload and download button with which a binary file can be uploaded/downloaded.
     */
    const renderByteArrayEditor = () =>
    {
        const contentType = (userPropertyProps.propertyDetails.userPropertyDetails?.type?.options["contentType"] as unknown as string);

        /**
         * Function that is called when the content of a file is loaded.
         * The event will contain the uploaded file in the following format:
         *
         * data:[<mediatype>][;base64],<data>
         *
         * See https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URLs
         *
         * @param {ProgressEvent<FileReader>} event - The event object containing information about the file loading process.
         */
        const onload = (event : ProgressEvent<FileReader>) =>
        {
            const dataUrl = event.target?.result;

            // extract the base64 encoded part
            onChangeValue((Assert.assertNotNullOrUndefined(dataUrl) as string).split(',')[1]);
        }

        const handleFileSelect = (fileList : FileList) =>
        {
            const fileReader = new FileReader();
            fileReader.onload = onload;
            fileReader.readAsDataURL(fileList[0])
        }

        /**
         * Executed when the download button is clicked
         */
        const onDownloadClicked = () =>
        {
            setBlob(new Blob([Base64.decode(Assert.assertNotNullOrUndefined(value))], {type: contentType}));
            setDownload(true);
        }

        /**
         * Is called when the download started. This should reset download to make sure the Download component is no
         * longer added.
         */
        const onDownload = () => {
            setDownload(false);
        }

        return(
            <div>
                <ButtonGroup>
                    <Button
                        size="sm"
                        variant="primary"
                        disabled={value === null || value?.length === 0 || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                        onClick={onDownloadClicked}
                    >
                        <DownloadIcon /> {t('user-property.download')}
                    </Button>

                    {download && blob !== undefined && (
                        <Download blob={blob}
                                  filename={userPropertyProps.propertyDetails.userPropertyDetails?.type?.options["filename"] as unknown as string}
                                  onDownload={onDownload} />
                    )}

                    <Form.Control
                        size="sm"
                        id={userPropertyProps.propertyDetails.propertyValue.name}
                        aria-describedby={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"}
                        type="file"
                        accept={contentType}
                        onChange={e => handleFileSelect((e.target as HTMLInputElement).files as FileList)}
                        disabled={isInherited || userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                        isInvalid={userPropertyProps.errorMessage !== undefined}
                        ref={propertyNodeRef as React.RefObject<HTMLInputElement>}
                    />
                </ButtonGroup>
            </div>
        )
    }

    const renderFormControl = () =>
    {
        const propertyType = userPropertyProps.propertyDetails.userPropertyDetails.type.type.toLowerCase();
        const editor = userPropertyProps.propertyDetails.userPropertyDetails.editor.toLowerCase();

        switch (propertyType) {
            case "boolean" :
                return renderCheckbox();
            case "string" :
                if (editor === "multiline") {
                    return renderMultilineStringEditor();
                }
                else if (editor === "password") {
                    return renderPasswordEditor();
                }
                else if (editor === "allowed-recipients") {
                    return renderAllowedRecipientsEditor();
                }
                else return renderStringEditor();
            case "integer" :
            case "long" :
                // a date is stored as a long value
                if (editor === "date") {
                    return renderDateEditor();
                }
                else return renderNumberEditor();
            case "enum":
                return renderEnumEditor();
            case "bytearray":
                return renderByteArrayEditor();
        }

        return (
            <RenderAlert message={`Unsupported propertyType: ${propertyType}, name: ${userPropertyProps.propertyDetails.propertyValue.name}`} variant="warning"/>
        )
    }

    const renderPreview = () =>
    {
        const propertyType = userPropertyProps.propertyDetails.userPropertyDetails.type.type.toLowerCase();
        const editor = userPropertyProps.propertyDetails.userPropertyDetails.editor.toLowerCase();

        switch (propertyType) {
            case "boolean" :
                return (
                    <span className={`ms-2 bg-text-nowrap body-secondary rounded-pill p-2 ${value === "true" ? "text-success fw-bold" : ""}`}>
                                    {value === "true" ? t("common.enabled") : t("common.disabled")}
                    </span>
                )
            case "string" :
            case "integer" :
            case "long" :
            case "enum":
                // a date is stored as a long value
                if (editor === "date") {
                    return value !== undefined && value !== null && value.length > 0 ? (
                        <span className="ms-2 text-nowrap bg-body-secondary rounded-pill p-2">
                            {DateUtils.timestampToISODateString(Number(value))}
                        </span>
                    ) : undefined
                }
                if (editor === "multiline") {
                    return value !== undefined && value !== null && value.length > 0 ? (
                        <span className="ms-2 text-nowrap bg-body-secondary rounded-pill p-2">
                            <MultiLineIcon />
                        </span>
                    ) : undefined
                }
                else if (editor === "password") {
                    return value !== undefined && value !== null && value.length > 0 ? (
                        <span className="ms-2 text-nowrap bg-body-secondary rounded-pill p-2">
                            ******
                        </span>
                    ) : undefined
                }
                else {
                    return value !== undefined && value !== null && value.length > 0 ? (
                        <span className="ms-2 text-nowrap bg-body-secondary rounded-pill p-2">
                            <Truncate value={value} maxLength={40} />
                        </span>
                    ) : undefined
                }
            case "bytearray":
                return undefined;
        }

        return (
            <RenderAlert message={`Unsupported propertyType: ${propertyType}, name: ${userPropertyProps.propertyDetails.propertyValue.name}`} variant="warning"/>
        )
    }

    return (
        <Form.Group className="mb-2 p-1">
            <Accordion>
                <Accordion.Item eventKey={userPropertyProps.propertyDetails.propertyValue.name}>
                    <Accordion.Header className="w-100">
                        <div className="fs-6 fw-bold d-flex flex-fill me-4">
                            <OverlayTrigger
                                placement="right"
                                delay={{ show: 1000, hide: 100 }}
                                overlay={
                                    <Popover>
                                        <Popover.Header className={"text-bg-dark"}>
                                            CLI Property
                                        </Popover.Header>
                                        <Popover.Body>
                                            {userPropertyProps.propertyDetails.propertyValue.name}
                                        </Popover.Body>
                                    </Popover>
                                }
                            >
                                <Form.Label htmlFor={userPropertyProps.propertyDetails.propertyValue.name} className="m-0">
                                    {tu('userproperty.' + userPropertyProps.propertyDetails.propertyValue.name)}
                                </Form.Label>
                            </OverlayTrigger>
                        </div>

                        <div className="me-4 text-muted d-none d-sm-block">
                            {renderPreview()}
                            {isInherited && <span className="border rounded-pill p-2">{t("common.inherited")}</span>}
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <Form.Text id={userPropertyProps.propertyDetails.propertyValue.name + "HelpBlock"} muted>
                            {tu(userPropertyProps.propertyDetails.userPropertyDetails.descriptionKey)}
                        </Form.Text>

                        <Form.Check
                            className="text-secondary"
                            id={`inherit-${userPropertyProps.propertyDetails.propertyValue.name}`}
                            label={t("common.inherit")}
                            checked={isInherited}
                            onChange={(e) => onChangeInherit(e.target.checked)}
                            disabled={userPropertyProps.propertyDetails.userPropertyDetails.readonly}
                        />

                        {renderFormControl()}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/*show an alert if a validation error occurred*/}
            <RenderValidationError errorMessage={userPropertyProps.errorMessage} />
        </Form.Group>
    );
}
